import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Aviso, Campo } from "../componentes/piezas";
import { SelectorIdioma, useIdioma } from "../lib/idioma";

/**
 * El formulario para pedir una prueba, en la web pública.
 *
 * Antes el botón abría el correo del visitante con un mailto:. Quien no tiene
 * el correo montado en ese ordenador —casi todo el mundo— se quedaba mirando
 * una pestaña en blanco, y lo que sí llegaba no quedaba apuntado en ningún
 * sitio. Ahora se guarda, avisa al proveedor y se contesta desde su panel con
 * un código.
 *
 * Pide lo justo para poder contestar bien: el club, quién escribe, cómo
 * localizarle y —opcional— cuántos socios tienen, que es lo que decide el
 * precio. Cada campo de más es alguien que no termina el formulario.
 *
 * Va con la letra y las clases de la web pública, no con la caja del login:
 * quien llega aquí todavía no es cliente.
 */

interface Datos {
  nombreClub: string;
  ciudad: string;
  nombre: string;
  email: string;
  telefono: string;
  sociosAproximados: string;
  mensaje: string;
  /** El cebo para robots. Una persona no lo ve; un robot lo rellena. */
  web: string;
}

export default function PedirPrueba() {
  const { t, idioma } = useIdioma();

  useEffect(() => {
    const anterior = document.title;
    document.title = t("titulo.prueba");
    return () => {
      document.title = anterior;
    };
  }, []);

  const [datos, setDatos] = useState<Datos>({
    nombreClub: "",
    ciudad: "",
    nombre: "",
    email: "",
    telefono: "",
    sociosAproximados: "",
    mensaje: "",
    web: "",
  });
  const [acepta, setAcepta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [enviada, setEnviada] = useState(false);

  const cambiar = (campo: keyof Datos, valor: string) =>
    setDatos((d) => ({ ...d, [campo]: valor }));

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await api("/auth/solicitar-prueba", {
        metodo: "POST",
        cuerpo: {
          nombreClub: datos.nombreClub,
          ciudad: datos.ciudad || null,
          nombre: datos.nombre,
          email: datos.email,
          telefono: datos.telefono || null,
          sociosAproximados: datos.sociosAproximados
            ? Number(datos.sociosAproximados)
            : null,
          mensaje: datos.mensaje || null,
          aceptaPrivacidad: true,
          // En el que esta leyendo la web: en ese se le contesta, y con ese
          // nace su club si acaba dandose de alta.
          idioma,
          web: datos.web || null,
        },
      });
      setEnviada(true);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("prueba.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="publica" lang={idioma}>
      <header className="publica-barra">
        <Link to="/" className="publica-marca">
          <img src="/asocis.png" alt="asocis" className="publica-logotipo" />
        </Link>
        <div className="fila" style={{ gap: 10 }}>
          <SelectorIdioma compacto />
          <Link to="/entrar" className="boton secundario">
            {t("web.entrar")}
          </Link>
        </div>
      </header>

      <main className="hoja hoja-formulario">
        {enviada ? (
          <>
            <h1 className="frase">{t("prueba.recibido")}</h1>
            <p>
              {t("prueba.recibidoTexto1")} <strong>{datos.email}</strong>{" "}
              {t("prueba.recibidoTexto2")} <strong>{datos.nombreClub}</strong>
              {t("prueba.recibidoTexto3")}
            </p>
            <p>
              <Link to="/">{t("prueba.volverWeb")}</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="frase">{t("prueba.titulo")}</h1>
            <p>{t("prueba.intro")}</p>

            <form className="formulario-prueba" onSubmit={enviar}>
              <Aviso tipo="error">{error}</Aviso>

              <Campo etiqueta={t("prueba.nombreClub")}>
                <input
                  value={datos.nombreClub}
                  onChange={(e) => cambiar("nombreClub", e.target.value)}
                  required
                  autoFocus
                  autoComplete="organization"
                />
              </Campo>

              <div className="formulario-prueba-dos">
                <Campo etiqueta={t("prueba.ciudad")}>
                  <input
                    value={datos.ciudad}
                    onChange={(e) => cambiar("ciudad", e.target.value)}
                    autoComplete="address-level2"
                  />
                </Campo>
                <Campo
                  etiqueta={t("prueba.socios")}
                  ayuda={t("prueba.sociosAyuda")}
                >
                  <input
                    type="number"
                    min={0}
                    max={100000}
                    inputMode="numeric"
                    value={datos.sociosAproximados}
                    onChange={(e) =>
                      cambiar("sociosAproximados", e.target.value)
                    }
                  />
                </Campo>
              </div>

              <Campo etiqueta={t("prueba.tuNombre")}>
                <input
                  value={datos.nombre}
                  onChange={(e) => cambiar("nombre", e.target.value)}
                  required
                  autoComplete="name"
                />
              </Campo>

              <div className="formulario-prueba-dos">
                <Campo
                  etiqueta={t("prueba.tuEmail")}
                  ayuda={t("prueba.tuEmailAyuda")}
                >
                  <input
                    type="email"
                    value={datos.email}
                    onChange={(e) => cambiar("email", e.target.value)}
                    required
                    autoComplete="email"
                  />
                </Campo>
                <Campo
                  etiqueta={t("prueba.telefono")}
                  ayuda={t("prueba.telefonoAyuda")}
                >
                  <input
                    type="tel"
                    value={datos.telefono}
                    onChange={(e) => cambiar("telefono", e.target.value)}
                    autoComplete="tel"
                  />
                </Campo>
              </div>

              <Campo
                etiqueta={t("prueba.mensaje")}
                ayuda={t("prueba.mensajeAyuda")}
              >
                <textarea
                  rows={4}
                  value={datos.mensaje}
                  onChange={(e) => cambiar("mensaje", e.target.value)}
                />
              </Campo>

              {/* El cebo: fuera de la vista y del tabulador. Ver el servidor. */}
              <div className="cebo" aria-hidden="true">
                <label>
                  Web
                  <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={datos.web}
                    onChange={(e) => cambiar("web", e.target.value)}
                  />
                </label>
              </div>

              <label className="casilla">
                <input
                  type="checkbox"
                  checked={acepta}
                  onChange={(e) => setAcepta(e.target.checked)}
                />
                <span>
                  {t("prueba.privacidad1")}{" "}
                  <Link to="/privacidad">{t("prueba.privacidad2")}</Link>{" "}
                  {t("prueba.privacidad3")}
                </span>
              </label>

              <button className="boton grande" disabled={enviando || !acepta}>
                {enviando ? t("general.enviando") : t("prueba.enviar")}
              </button>
            </form>
          </>
        )}
      </main>

      <footer className="publica-pie">
        <span>{t("web.pie")}</span>
        <span className="publica-pie-enlaces">
          <Link to="/aviso-legal">{t("web.pie.avisoLegal")}</Link>
          <Link to="/privacidad">{t("web.pie.privacidad")}</Link>
        </span>
      </footer>
    </div>
  );
}
